import './vehicle-modal.scss';

import { notification } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';

import {
  Autocomplete,
  Button,
  DatePickerField,
  Input,
  Modal
} from '@/components/Shared/ui';
import { BankNames, DictTypes } from '@/constants';
import { useAppSelector } from '@/store';

import { getVisibleVehicleInfoFieldsMap } from '../../helpers';
import { TechPassportPreviewProvider } from './entities';
import useSaveVehicleInfo from './hooks/useSaveVehicleInfo';
import useVehicleModalForm from './hooks/useVehicleModalForm';
import type { VehicleFormInterface } from './types/vehicle-form';

const frozenFieldsStatuses = [
  'financed',
  'waiting_of_financing',
  'waiting_for_upload_files',
  'on_rework',
  'refund_in_progress',
  'refunded'
];

interface IProps {
  isOpened: boolean;
  onClose: () => void;
}

const VehicleModal: React.FC<IProps> = ({ isOpened, onClose }) => {
  const {
    userAccesses: {
      autoCredit: { canChangeVehicleInfo }
    },
    userInfo
  } = useAppSelector((state) => state.userInfo);
  const { dicts } = useAppSelector((state) => state.dict);
  const { selectedBank, isEuBankOfflineProcess } = useAppSelector(
    (state) => state.financingStep
  );
  const { orderApp } = useAppSelector((state) => state.newOrder);

  const [isTechPassportPreviewVisible, setIsTechPassportPreviewVisible] =
    useState(false);
  const draftData = useRef<VehicleFormInterface | null>(null);
  const ignoreNextDraftNotifications = useRef<boolean>(false);

  const isEuBankV2 = selectedBank?.bank.name === BankNames.euBankV2;
  const isElectricVehicle =
    String(orderApp?.productInfo.product.engineType) === '4';
  const isFieldsFrozen =
    !canChangeVehicleInfo ||
    (isEuBankV2
      ? frozenFieldsStatuses.includes(selectedBank?.status.code || '')
      : selectedBank?.status.code === 'financed');

  const {
    methods: {
      control,
      formState: { errors },
      handleSubmit,
      clearErrors,
      getValues
    },
    applyStoredValues,
    compareValues,
    getVisibleFields,
    removeLocalStorage,
    storageKey,
    storedValues
  } = useVehicleModalForm({ isOpened });
  const { saveLoading, onSubmit } = useSaveVehicleInfo({ onClose, storageKey });

  const [licensePlate, registerNum] = useWatch({
    control,
    name: ['licensePlate', 'registerNum']
  });

  const handleClose = () => {
    onClose();
    clearErrors();
    setIsTechPassportPreviewVisible(false);
    ignoreNextDraftNotifications.current = false;
    draftData.current = null;
  };

  const handleApplyDraft = () => {
    if (!storageKey) return;
    notification.destroy(storageKey);
    applyStoredValues();
  };

  const handleRejectDraft = () => {
    if (!storageKey) return;
    notification.destroy(storageKey);
    removeLocalStorage();
  };

  const registerCompanies = useMemo(
    () =>
      (dicts?.[DictTypes.VehicleRegistrationCompanies]?.content || []).map(
        (item) => ({ value: String(item.code), label: item.name })
      ),
    [dicts]
  );

  const displayVehicleFieldsMap = useMemo(() => {
    return getVisibleVehicleInfoFieldsMap({
      bankName: selectedBank?.bank.name as BankNames,
      isEuBankOfflineProcess
    });
  }, [selectedBank?.bank.name, isEuBankOfflineProcess]);

  useEffect(() => {
    if (
      isOpened &&
      userInfo &&
      dicts &&
      storedValues &&
      !ignoreNextDraftNotifications.current
    ) {
      const currentValues = getValues();
      const hasDifferences = compareValues(storedValues, currentValues);

      if (!hasDifferences) {
        if (storageKey) {
          sessionStorage.removeItem(storageKey);
        }
        return;
      }

      ignoreNextDraftNotifications.current = true;

      notification.info({
        key: storageKey,
        title: 'У вас остались данные ТС, введенные в прошлый раз',
        description: 'Хотите восстановить их?',
        showProgress: true,
        duration: 5,
        onClose: () => {
          if (storageKey) {
            sessionStorage.removeItem(storageKey);
          }
        },
        actions: [
          <Button
            className="vehicle-modal__saved-data-action"
            key="cancel-button"
            onClick={() => handleRejectDraft()}
            size="small"
          >
            Нет
          </Button>,
          <Button
            className="vehicle-modal__saved-data-action"
            key="accept-button"
            onClick={() => handleApplyDraft()}
            size="small"
            variant="filled-red"
          >
            Да
          </Button>
        ]
      });
    }
  }, [
    isOpened,
    userInfo,
    dicts,
    storageKey,
    getValues,
    compareValues,
    getVisibleFields,
    storedValues
  ]);

  return (
    <Modal
      centered
      wrapClassName="vehicle-modal-wrapper"
      mask={{ closable: true }}
      className="vehicle-modal"
      title="Данные ТС"
      open={isOpened}
      onClose={handleClose}
      width={isTechPassportPreviewVisible ? 1100 : undefined}
    >
      <TechPassportPreviewProvider
        isPreviewVisible={isTechPassportPreviewVisible}
        onPreviewVisibleChange={setIsTechPassportPreviewVisible}
      >
        <form className="vehicle-modal__form" onSubmit={handleSubmit(onSubmit)}>
          {(displayVehicleFieldsMap.licensePlate ||
            (isEuBankV2 && isFieldsFrozen && !!licensePlate)) && (
            <Controller
              control={control}
              name="licensePlate"
              rules={{
                required: 'Пожалуйста, введите данные',
                minLength: { value: 1, message: 'Пожалуйста, введите данные' }
              }}
              render={({ field, fieldState: { error } }) => (
                <div className="vehicle-modal__item vehicle-modal__item--full">
                  <Input
                    {...field}
                    label="Государственный номерной знак"
                    value={field.value ?? ''}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[а-яА-ЯЁё]/g, '');
                      if (value.length > 8) return;
                      field.onChange(value.toUpperCase());
                    }}
                    error={error?.message}
                    isFrozen={isFieldsFrozen}
                    isRequired
                  />
                </div>
              )}
            />
          )}

          {displayVehicleFieldsMap.vin && (
            <Controller
              control={control}
              name="vin"
              rules={{
                required: 'Пожалуйста, введите данные',
                minLength: {
                  value: 17,
                  message: 'VIN код должен состоять из 17 символов'
                },
                validate: (value) => {
                  if (/I|O|Q/.test(value))
                    return 'В поле VIN не должно быть букв I, O, Q';
                }
              }}
              render={({ field, fieldState: { error } }) => (
                <div className="vehicle-modal__item vehicle-modal__item--full">
                  <Input
                    {...field}
                    label="Идентификационный номер машины (VIN)"
                    value={field.value ?? ''}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^A-Za-z0-9]/g, '');
                      if (value.length > 17) return;
                      field.onChange(value.toUpperCase());
                    }}
                    error={error?.message}
                    isFrozen={isFieldsFrozen}
                    isRequired
                  />
                </div>
              )}
            />
          )}

          {displayVehicleFieldsMap.bodyNum && (
            <Controller
              control={control}
              name="bodyNum"
              rules={{
                required: 'Пожалуйста, введите данные',
                minLength: {
                  value: 1,
                  message: 'Пожалуйста, введите данные'
                }
              }}
              render={({ field, fieldState: { error } }) => (
                <div className="vehicle-modal__item vehicle-modal__item--full">
                  <Input
                    {...field}
                    label="Номер кузова"
                    value={field.value ?? ''}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[а-яА-ЯЁё]/g, '');
                      if (value.length > 17) return;
                      field.onChange(value.toUpperCase());
                    }}
                    error={error?.message}
                    isFrozen={isFieldsFrozen}
                    isRequired
                  />
                </div>
              )}
            />
          )}

          {displayVehicleFieldsMap.paymentDate && (
            <Controller
              control={control}
              name="paymentDate"
              rules={{
                required: 'Пожалуйста, введите данные',
                validate: (value) => {
                  const shouldValidate = value.length === '01.01.2020'.length;
                  if (!shouldValidate) return 'Неверный формат даты';
                }
              }}
              render={({ field, fieldState: { error } }) => (
                <div className="vehicle-modal__item vehicle-modal__item--half">
                  <DatePickerField
                    {...field}
                    label="Дата первоначального взноса"
                    error={error?.message}
                    viewFormat="DD.MM.YYYY"
                    isFrozen={isFieldsFrozen}
                    isRequired
                  />
                </div>
              )}
            />
          )}

          {displayVehicleFieldsMap.registerDate && (
            <Controller
              control={control}
              name="registerDate"
              rules={{
                required: 'Пожалуйста, введите данные',
                validate: (value) => {
                  const shouldValidate = value.length === '01.01.2020'.length;
                  if (!shouldValidate) return 'Неверный формат даты';
                }
              }}
              render={({ field, fieldState: { error } }) => (
                <div className="vehicle-modal__item vehicle-modal__item--half">
                  <DatePickerField
                    {...field}
                    label="Дата свид. о регистрации ТС"
                    error={error?.message}
                    viewFormat="DD.MM.YYYY"
                    isFrozen={isFieldsFrozen}
                    isRequired
                  />
                </div>
              )}
            />
          )}

          {displayVehicleFieldsMap.registerCompany && (
            <Controller
              control={control}
              name="registerCompany"
              rules={{
                required: 'Пожалуйста, введите данные',
                minLength: {
                  value: 1,
                  message: 'Пожалуйста, введите данные'
                }
              }}
              render={({ field, fieldState: { error } }) => (
                <div className="vehicle-modal__item vehicle-modal__item--full">
                  <Autocomplete
                    {...field}
                    label="Компания регистратор автомобиля"
                    value={field.value ?? ''}
                    options={registerCompanies}
                    error={error?.message}
                    isFrozen={isFieldsFrozen}
                    isRequired
                  />
                </div>
              )}
            />
          )}

          {displayVehicleFieldsMap.engineCapacity && (
            <Controller
              control={control}
              name="engineCapacity"
              rules={{
                required: 'Пожалуйста, введите данные',
                minLength: {
                  value: 1,
                  message: 'Пожалуйста, введите данные'
                },
                validate: (value) => {
                  if (
                    !isElectricVehicle &&
                    (Number.isNaN(value) || +value < 500)
                  )
                    return 'Минимальный объем в кубических сантиметрах - 500';
                }
              }}
              render={({ field, fieldState: { error } }) => (
                <div className="vehicle-modal__item vehicle-modal__item--half">
                  <Input
                    {...field}
                    type="tel"
                    label="Объем двигателя"
                    value={field.value ?? ''}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      if (value.length > 4) return;
                      field.onChange(value);
                    }}
                    error={error?.message}
                    isFrozen={isFieldsFrozen || isElectricVehicle}
                    isRequired
                  />
                </div>
              )}
            />
          )}

          {displayVehicleFieldsMap.carColor && (
            <Controller
              control={control}
              name="carColor"
              rules={{
                required: 'Пожалуйста, введите данные',
                minLength: {
                  value: 1,
                  message: 'Пожалуйста, введите данные'
                }
              }}
              render={({ field, fieldState: { error } }) => (
                <div className="vehicle-modal__item vehicle-modal__item--half">
                  <Input
                    {...field}
                    label="Цвет авто"
                    value={field.value ?? ''}
                    error={error?.message}
                    isFrozen={isFieldsFrozen}
                    isRequired
                  />
                </div>
              )}
            />
          )}

          {(displayVehicleFieldsMap.registerNum ||
            (isEuBankV2 && isFieldsFrozen && !!registerNum)) && (
            <Controller
              control={control}
              name="registerNum"
              rules={{
                required: 'Пожалуйста, введите данные',
                minLength: { value: 1, message: 'Пожалуйста, введите данные' }
              }}
              render={({ field, fieldState: { error } }) => (
                <div className="vehicle-modal__item vehicle-modal__item--full">
                  <Input
                    {...field}
                    label="Номер свидетельства о регистрации ТС"
                    value={field.value ?? ''}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[а-яА-ЯЁё]/g, '');
                      if (value.length > 10) return;
                      field.onChange(value.toUpperCase());
                    }}
                    error={error?.message}
                    isFrozen={isFieldsFrozen}
                    isRequired
                  />
                </div>
              )}
            />
          )}

          {!isFieldsFrozen && (
            <div className="vehicle-modal__actions">
              <Button type="button" onClick={() => handleClose()}>
                Отмена
              </Button>
              <Button
                type="submit"
                variant="filled-red"
                disabled={Object.values(errors).length !== 0}
                isLoading={saveLoading}
              >
                Добавить
              </Button>
            </div>
          )}
        </form>
      </TechPassportPreviewProvider>
    </Modal>
  );
};

export default VehicleModal;
